import React from "react";
import { Control, FieldValues, FieldPath } from "react-hook-form";
import {
  DoubleMaterialityStakeholderESRSArea,
  StakeholderDto,
} from "@green0meter/api-client";
import { capitalize } from "lodash";

import { ESRS_TYPES } from "../../../StakeholdersTable/StakeholdersTable.constants";
import SelectableChip from "../SelectableChip/SelectableChip";
import { ESRS_COLORS } from "../../../ESRS.constants";

import { Stack, Typography, FormControl, Select, MenuItem } from "@components/base";
import LabeledControllerField from "@components/common/LabeledControllerField/LabeledControllerField";

interface ESRSAreasFieldProps<
  T extends FieldValues & { esrsAreas: StakeholderDto["esrsAreas"] },
> {
  control: Control<T, Record<string, unknown>, unknown>;
  disabled?: boolean;
}

const ESRSAreasField = <
  T extends FieldValues & {
    esrsAreas: StakeholderDto["esrsAreas"];
  },
>({
  control,
  disabled = false,
}: ESRSAreasFieldProps<T>): React.ReactElement => {
  return (
    <LabeledControllerField<T, FieldPath<T>>
      label={"ESRS area"}
      name={"esrsAreas" as FieldPath<T>}
      control={control}
      disabled={disabled}
      render={({ field, error, disabled }) => (
        <FormControl fullWidth error={Boolean(error)}>
          <Select
            {...field}
            multiple
            disabled={disabled}
            variant="filled"
            renderValue={(selected) => (
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {(selected as DoubleMaterialityStakeholderESRSArea[]).map(
                  (value) => {
                    const esrsType = ESRS_TYPES[value];
                    const colors = ESRS_COLORS[esrsType];

                    return (
                      <SelectableChip
                        key={value}
                        label={esrsType}
                        backgroundColor={colors.background}
                        borderColor={colors.border}
                        textColor={colors.color}
                        size="medium"
                        onDelete={() => {
                          const newSelected = (
                            field.value as DoubleMaterialityStakeholderESRSArea[]
                          ).filter((item) => item !== value);

                          field.onChange(newSelected);
                        }}
                      />
                    );
                  },
                )}
              </Stack>
            )}
          >
            {(
              Object.values(
                DoubleMaterialityStakeholderESRSArea,
              )
            ).map((area) => {
              const esrsType = ESRS_TYPES[area];

              const text = area.replace(/_/g, " ").trim();
              const displayText =
                esrsType === "OTHER"
                  ? "Other"
                  : `${esrsType}: ${capitalize(text)}`;

              return (
                <MenuItem key={area} value={area}>
                  {displayText}
                </MenuItem>
              );
            })}
          </Select>
          {error && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
              {error.message}
            </Typography>
          )}
        </FormControl>
      )}
    />
  );
};

export default ESRSAreasField;
